import { makeStyles, Snackbar, Tab, Typography } from '@material-ui/core';
import { Alert } from '@material-ui/lab';
import { AttributesMixin, define, Events, Linked, memberOf, Store, type } from '@type-r/models';
import { useEvent, useIO, useLinked, useModel } from '@type-r/react';
import { CompanyModel, globalStore, InmatchPermission, localUserInfo, PlatformCompany } from 'api';
import { Activity } from 'api/activity';
import { AppLink, asCurrency, asDate, asTimelessDate, Flex, Loading, Menu, MenuItem, Page, PageContainer, PageTab, PrimaryButton, SearchInput, SelectSortBy, SmallTile, TileField, TilesList, TransparentTabs, useLinkedLocation } from 'lqx-react';
import * as React from 'react';
import { useEffect } from 'react';
import { RemittanceAdvice, RemittanceAdvicesSummary } from 'src/app/pages/inmatch/api';
import { CreateRADialog } from '../invoices/createRADialog';
import { RemittanceAdviceDetails } from './details';
import { ArchiveDialog, UnresolvedRemittanceAdvices } from './unresolved';
import WarningIcon from '@material-ui/icons/WarningTwoTone';
import { Can } from 'src/app/services/can';
import { UserEntitlementCatalogs, UserEntitlementCatalogValue } from 'src/app/models/UserEntitlements';

interface State extends AttributesMixin<typeof State>{}

const sortRemittanceOptions = {
    executionDate: 'Payment Execution Date',
    estimatedPaymentDate: 'Estimated Payment Date',
    createdOn: 'Created Date',
};

@define class State extends Store {
    static attributes = {
        remittanceAdvices : [ RemittanceAdvice ],
        activities : [Activity],
        selected : memberOf('remittanceAdvices', RemittanceAdvice),
        filter : '',
        category: 'resolved',
        sortBy : {
            attr: 'executionDate',
            asc : false
        },
        companies : [ PlatformCompany ],
        remittanceAdviceToBeArchived: type(RemittanceAdvice).null,
    }
    
    async fetchAll( category : string ){
        this.companies = globalStore.platformCompanies;
        
        await Promise.all([
            this.remittanceAdvices.fetch({ params : { category }}),
        ]);

        return this;
    }
}

export const RemittancePage = ({ pathLength = 4, noContainer = false, navigateToOnSave = undefined, showSuccessSnackBar = false }) => {
    const Container = noContainer ? React.Fragment : PageContainer;

    const $category = useLinkedLocation(3, "unmatched"),
    { value: category } = $category;

    const $categoryLoading = useLinked(false);

    return (
        (Can(UserEntitlementCatalogs.BankEntryView) ||
                Can(UserEntitlementCatalogs.BankEntryWrite) ||
                Can(UserEntitlementCatalogs.BankEntryConfirm)) &&
        <Container
            header={
                    <RemittanceAdvicePageTabs $category={ $category } $loading={ $categoryLoading } />
                }
        >
        {
          category === 'unresolved' || category === 'archived' ?
            <UnresolvedRemittanceAdvices $category={$category} $loading={ $categoryLoading } /> :
            <ResolvedRemittanceAdvices pathLength={pathLength} $category={ $category } $loading={ $categoryLoading } navigateToOnSave={navigateToOnSave} showSuccessSnackBar={showSuccessSnackBar} />
        }
        </Container>
    )
}

const ResolvedRemittanceAdvices = ({ pathLength = 4, $category, navigateToOnSave, showSuccessSnackBar, $loading }) => {
    const state = useModel( State );
    const { value: category } = $category;
    const $refreshState = useLinked(false);

    // Map 3rd URL name in path as a linked string.
    const $isEditing = useLinkedLocation( pathLength );

    const isReady = useIO( async () => {
        $loading.true();
        await state.fetchAll( category );
        $loading.false();

        if ($isEditing.value) {
            state.selected = state.remittanceAdvices.filter(ra => ra.id.toString() === $isEditing.value)[0];
        }
    
    }, [category, $refreshState.value]);

    const sortedAndFiltered = React.useMemo( () => {
        const { attr, asc } = state.sortBy;
        return state.remittanceAdvices
            .filter(ra => ra.customer && ra.filter(state.filter) )
            .sort( RemittanceAdvice.comparator( attr as any, asc ) );
    },
    [ state.remittanceAdvices['_changeToken'], state.sortBy['_changeToken'], state.filter, isReady ]);

    React.useEffect(() => {
        if (!$isEditing.value) {
            state.selected = null;
        }
    }, [$isEditing.value]);

    const remittanceAdviceArchived = (remittanceAdviceId) => {
        state.$.selected.set(null);
        state.remittanceAdvices.remove(remittanceAdviceId);
    }
    
    const $openSnackBar = useLinked(null);

    const onSuccess = (ra) => {
        if ($isEditing.value) {
            $openSnackBar.set($isEditing.value);
            $refreshState.set(!$refreshState.value);
        } else {
            Events.trigger("raSummary:refresh");

            state.selected = ra;
            $isEditing.set(ra.id);
        }
    }
    
    const $showCreateRA = useLinked(false);

    useEffect(() => {
        if (isReady && !sortedAndFiltered.some(ra => ra === state.selected) && category !== 'details') {
            $isEditing.value = null;
            state.selected = null;
        }

        if (!$isEditing.value && sortedAndFiltered.length) {
            state.selected = sortedAndFiltered[0];
            $isEditing.set(state.selected.id);
        }
    }, [sortedAndFiltered]);

    return (
        isReady ?
        <Page
            menuWidth={530}
            maxWidth="xl"
            left={
                <>
                     <SearchInput
                        placeholder="Filter by amount, company, reference number, invoice number, execution date"
                        $value={ state.$.filter }
                    />
                    <Flex row>
                        <SelectSortBy $attr={ state.sortBy.$.attr } $asc={ state.sortBy.$.asc } options={ sortRemittanceOptions } />

                        <Flex grow />
                        {Can(UserEntitlementCatalogs.BankEntryWrite) &&
                            <PrimaryButton onClick={() => {
                                state.$.selected.null();
                                $isEditing.null();
                                $showCreateRA.true();
                            }}>
                                Create
                            </PrimaryButton>
                        }
                    </Flex>

                    
                    {sortedAndFiltered.length ? (
                        <TilesList
                        items={sortedAndFiltered}
                        itemSize={64}
                        renderer={RemittanceTile( $isEditing, $category, state.$.remittanceAdviceToBeArchived, state.$.selected )}
                        selectedItem={state.selected}
                        />
                    ) : (
                        <Flex center stretch>
                        <Typography variant="h4">No results found.</Typography>
                        </Flex>
                    )}
                </>
            }
        >

            { $isEditing.value && isReady ?
                <RemittanceAdviceDetails $remittanceAdviceId={ $isEditing } store={state} onSuccess={onSuccess} navigateToOnSave={navigateToOnSave} />
                :  (
                <Flex center stretch>
                    Select the Remittance Advice
                </Flex>
            )}

            <Snackbar
                anchorOrigin={{ horizontal: 'center', vertical: 'top' }}
                open={showSuccessSnackBar && $openSnackBar.value}
                autoHideDuration={5000}
                onClose={() => $openSnackBar.false()}
            >
                <Alert severity="success">
                    <AppLink href={`/remittance-advices/details/${$openSnackBar.value}`}>Remittance Advice #{$openSnackBar.value} saved</AppLink>
                </Alert>
            </Snackbar>
           

        {state.remittanceAdviceToBeArchived ? (
            <ArchiveDialog
                $remittanceAdvice={ state.$.remittanceAdviceToBeArchived }
                remittanceAdviceArchived={remittanceAdviceArchived}
            />
            ) : (
            undefined
        )}

        {$showCreateRA.value ? <CreateRADialog $isOpen={$showCreateRA} companies={state.companies} onRACreated={onSuccess} 
            onSuccess={() => {
                $refreshState.set(!$refreshState.value)
            }}/> : undefined}

        </Page> :
        <Loading />
        
    )
}

const RemittanceTile = ($isEditing, $category, $remittanceAdviceToBeArchived, $selected) => ({ data, style, index }) => {
    const remittanceAdvice : RemittanceAdvice = data.items[ index ];

    const { isMissingSupplierName} = remittanceAdvice;

    return (
        <div style={style}>
            <SmallTile
                isSelected={ $selected.value === remittanceAdvice }
                style={ style }
                color={ 'green' }
                onClick={ () => {
                    $isEditing.set(remittanceAdvice.id);
                    $selected.set(remittanceAdvice);
                }}
                action={
                    <>
                        { isMissingSupplierName ? <WarningIcon titleAccess='Supplier is missing or multiple supplier are available in the RA.' /> : undefined }
                        { !remittanceAdvice.paymentId && Can(UserEntitlementCatalogs.BankEntryWrite) ?
                        <Menu>
                            <MenuItem onSelect={() => {
                                $remittanceAdviceToBeArchived.set(remittanceAdvice);
                            }}>
                                Archive
                            </MenuItem>
                        </Menu> : undefined}
                    </>
                }
                footer={
                    <>
                        { remittanceAdvice.paymentId ? 
                            <TileField label="Payment">
                                <AppLink 
                                    href={
                                        remittanceAdvice.isReconciled ?
                                            `/bank-transactions/details/${remittanceAdvice.paymentId}` : 
                                            `/matching/unreconciled?paymentId=${remittanceAdvice.paymentId}`
                                    } 
                                    returnUrl
                                >
                                    #{ remittanceAdvice.paymentId }
                                </AppLink>
                            </TileField>
                        : null }
                        <TileField label="Amount">
                            { asCurrency( remittanceAdvice.totalInvoiceAmount, remittanceAdvice.currency ) }
                        </TileField>
            
                        <TileField label="Execution Date">
                            { asTimelessDate( remittanceAdvice.executionDate ) }
                        </TileField>

                        <Flex grow />
                        
                    </>
                }
            >
                <div>{remittanceAdvice.customer && remittanceAdvice.customer.name}</div>
            </SmallTile>
        </div>
    )
}

const RemittanceAdvicePageTabs = ({
    $category,
    $loading
  }: {
    $category: Linked<string>;
    $loading: Linked<boolean>;
  }) => {
    const summary = useModel(RemittanceAdvicesSummary),
      { count, unresolvedCount, unmatchedCount, unreconciledCount, archivedCount, reconciledCount } = summary;
  
    useEffect(() => {
      summary.fetch();
    }, [$category.value]);
  
    useEvent(Events, "raSummary:refresh", () => {
      summary.fetch();
    });
    const classes = useStyles();

  return (
    <TransparentTabs $value={$category}>
        <PageTab value="unmatched" title="Unmatched" label={String(unmatchedCount)} disabled={$loading.value} />
        <PageTab value="unreconciled" title="Matched" label={String(unreconciledCount)} disabled={$loading.value}/>
        <PageTab value="reconciled" title="Reconciled" label={String(reconciledCount)} disabled={$loading.value} />
        <Tab
            aria-hidden="true"
            key="disabled"
            value={"separator"}
            disabled
            classes={{
            root: classes.separator
            }}
            label={<span className={classes.separatorElement} />}
        />
        <PageTab value="unresolved" title="Unresolved" label={String(unresolvedCount)} disabled={$loading.value} />
        <PageTab value="archived" title="Archived" label={String(archivedCount)} disabled={$loading.value} />

    </TransparentTabs>
  );
};

const useStyles = makeStyles({
    separator: {
      width: 35,
      minWidth: 20
    },
  
    separatorElement: {
      borderLeft: "solid",
      borderWidth: 1,
      borderColor: "white",
      height: 41,
      marginTop: 7
    }
  });
